import type { MediaItem, MediaType } from "@/types/media";

export const TYPE_LABEL: Record<MediaType, string> = {
  teleplay: "电视剧",
  movie: "电影",
  variety: "综艺",
};

export const TYPE_PATH: Record<MediaType, string> = {
  teleplay: "teleplay",
  movie: "movie",
  variety: "variety",
};

export const NAV_CATEGORIES: { type: MediaType; label: string; icon: string }[] = [
  { type: "teleplay", label: "电视剧", icon: "ri-tv-2-line" },
  { type: "movie", label: "电影", icon: "ri-film-line" },
  { type: "variety", label: "综艺", icon: "ri-mic-2-line" },
];

export function isMediaType(value: string | undefined): value is MediaType {
  return value === "teleplay" || value === "movie" || value === "variety";
}

export function getTypeLabel(type: MediaType): string {
  return TYPE_LABEL[type];
}

export function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

export function getEpisodeCount(item: MediaItem): number {
  return item.episodeCount > 0 ? item.episodeCount : 1;
}

export function getEpisodes(item: MediaItem): number[] {
  const total = getEpisodeCount(item);
  return Array.from({ length: total }, (_, i) => i + 1);
}

export function buildEpisodeGroups(item: MediaItem): { name: string; episodes: number[] }[] {
  if (item.seasons && item.seasons.length > 0) {
    let cursor = 0;
    return item.seasons.map((season) => {
      const episodes = Array.from({ length: season.count }, (_, i) => cursor + i + 1);
      cursor += season.count;
      return { name: season.name, episodes };
    });
  }
  return [{ name: "剧集列表", episodes: getEpisodes(item) }];
}

export function findMediaById(list: MediaItem[], id: string | undefined): MediaItem | undefined {
  if (!id) return undefined;
  return list.find((item) => item.id === id);
}

export function getAllGenres(list: MediaItem[], type?: MediaType): string[] {
  const set = new Set<string>();
  list
    .filter((item) => (type ? item.type === type : true))
    .forEach((item) => item.genres.forEach((g) => set.add(g)));
  return Array.from(set);
}

export function getAllRegions(list: MediaItem[], type?: MediaType): string[] {
  const set = new Set<string>();
  list
    .filter((item) => (type ? item.type === type : true))
    .forEach((item) => set.add(item.region));
  return Array.from(set);
}

export function getAllYears(list: MediaItem[], type?: MediaType): number[] {
  const set = new Set<number>();
  list
    .filter((item) => (type ? item.type === type : true))
    .forEach((item) => set.add(item.year));
  return Array.from(set).sort((a, b) => b - a);
}

export interface FilterOptions {
  genres: string[];
  years: number[];
  regions: string[];
  sort: "hot" | "new" | "rating";
}

export function filterAndSort(list: MediaItem[], options: FilterOptions): MediaItem[] {
  const result = list.filter((item) => {
    const genreOk = options.genres.length === 0 || item.genres.some((g) => options.genres.includes(g));
    const yearOk = options.years.length === 0 || options.years.includes(item.year);
    const regionOk = options.regions.length === 0 || options.regions.includes(item.region);
    return genreOk && yearOk && regionOk;
  });

  return result.sort((a, b) => {
    if (options.sort === "new") {
      if (b.year !== a.year) return b.year - a.year;
      return b.rating - a.rating;
    }
    if (options.sort === "rating") return b.rating - a.rating;
    return b.views - a.views;
  });
}

export function getRecommendations(list: MediaItem[], current: MediaItem, limit = 6): MediaItem[] {
  return list
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const genreScore = item.genres.filter((g) => current.genres.includes(g)).length;
      const actorScore = item.actors.filter((a) => current.actors.includes(a)).length;
      const typeScore = item.type === current.type ? 1 : 0;
      return { item, score: genreScore * 3 + actorScore * 4 + typeScore };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function searchMedia(list: MediaItem[], keyword: string): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return list;
  return list.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      item.region,
      ...item.genres,
      ...item.actors,
      ...item.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}